import { useState } from 'react'
import { useBrand } from '@/brand/BrandProvider'
import { ImageUploadField } from '@/components/ImageUploadField'
import { Spinner } from '@/components/ui'
import { api } from '@/lib/api'
import { DEFAULT_ACCENT, DEFAULT_LOGO_URL, readableTextColor } from '@/lib/whitelabel'
import { AdminSection } from './AdminSection'

interface Draft {
  appName: string
  primaryColor: string
  logoUrl: string
  shortDomain: string
}

export function AdminBrand() {
  const brand = useBrand()
  const [draft, setDraft] = useState<Draft>({
    appName: brand.appName === 'Power Seller' ? '' : brand.appName,
    primaryColor: brand.primaryColor || DEFAULT_ACCENT,
    // brand.logoUrl siempre resuelve a algo (el ícono de Power Seller como default desde la
    // Fase 3): si es exactamente ese default, el campo arranca vacío — si no, el admin nunca
    // tocó el logo y guardar sin querer congelaría la URL del asset default como si fuera su
    // propio override (y esa URL lleva un hash de build, se rompe en el próximo deploy).
    logoUrl: brand.logoUrl === DEFAULT_LOGO_URL ? '' : brand.logoUrl ?? '',
    shortDomain: brand.shortDomain ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }))
  const previewName = draft.appName.trim() || 'Power Seller'
  const contrast = readableTextColor(draft.primaryColor)

  async function save() {
    setSaving(true)
    setSaved(false)
    setErr(null)
    try {
      await api.put<{ ok: boolean }>('/brand', {
        appName: draft.appName.trim() || undefined,
        primaryColor: draft.primaryColor || undefined,
        logoUrl: draft.logoUrl.trim() || undefined,
        shortDomain: draft.shortDomain.trim() || undefined,
      })
      brand.refresh() // re-lee /api/brand → aplica en toda la app sin recargar
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminSection title="Marca (White Label)">
      <div className="space-y-5">
        {/* Preview en vivo (aislado) */}
        <div>
          <p className="field-label">Vista previa</p>
          <div className="overflow-hidden rounded-2xl border border-line">
            <div className="flex items-center justify-between px-4 py-3">
              {draft.logoUrl.trim() ? (
                <img src={draft.logoUrl} alt={previewName} className="h-8 w-auto object-contain" />
              ) : (
                <span
                  className="flex h-8 items-center rounded-lg px-2.5 text-sm font-bold"
                  style={{ backgroundColor: draft.primaryColor, color: contrast }}
                >
                  {previewName}
                </span>
              )}
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold"
                style={{ backgroundColor: '#f1f5f9', color: draft.primaryColor }}
              >
                MV
              </div>
            </div>
            <div className="px-4 pb-4">
              <button
                className="w-full rounded-xl py-3 text-sm font-semibold"
                style={{ backgroundColor: draft.primaryColor, color: contrast }}
                type="button"
              >
                Botón de ejemplo
              </button>
            </div>
          </div>
        </div>

        {/* Nombre */}
        <div>
          <label className="field-label">Nombre de la app (máx. 30)</label>
          <input
            className="field-input"
            maxLength={30}
            value={draft.appName}
            onChange={(e) => set('appName', e.target.value)}
            placeholder="Power Seller"
          />
        </div>

        {/* Color */}
        <div>
          <label className="field-label">Color primario</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              className="h-11 w-14 cursor-pointer rounded-lg border border-line bg-surface"
              value={/^#[0-9a-f]{6}$/i.test(draft.primaryColor) ? draft.primaryColor : DEFAULT_ACCENT}
              onChange={(e) => set('primaryColor', e.target.value)}
            />
            <input
              className="field-input flex-1"
              value={draft.primaryColor}
              onChange={(e) => set('primaryColor', e.target.value)}
              placeholder="#6d28d9"
            />
          </div>
        </div>

        {/* Logo */}
        <ImageUploadField
          label="Logo (PNG/SVG)"
          value={draft.logoUrl}
          onChange={(url) => set('logoUrl', url)}
          placeholder="https://cdn.tienda.com/logo.png"
          hint="Subí un archivo (máx. 5 MB) o pegá una URL. Si se deja vacío, se usa el ícono de Power Seller."
        />

        {/* Dominio shortlinks (opcional) */}
        <div>
          <label className="field-label">Dominio de shortlinks (opcional)</label>
          <input
            className="field-input"
            value={draft.shortDomain}
            onChange={(e) => set('shortDomain', e.target.value)}
            placeholder="ps.tienda.com"
          />
        </div>

        {err && <div className="rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">{err}</div>}

        <button className="btn-primary btn-block" onClick={save} disabled={saving}>
          {saving ? <Spinner className="h-4 w-4" /> : saved ? '✅ Guardado' : 'Guardar cambios'}
        </button>
      </div>
    </AdminSection>
  )
}
